import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  AreaChart, Area,
} from 'recharts';
import { youtubeAPI } from '../../services/api';
import SectionTitle from '../crt/SectionTitle';
import Panel from '../crt/Panel';

const COLORS = ['#00ff41', '#00ffff', '#ff6600', '#ffb000', '#ff3344', '#5aaa5a', '#9c27b0', '#1976d2'];

const fmt = (n: number) => (n ?? 0).toLocaleString();

const DiscoveryInsights: React.FC = () => {
  const { data: funnel } = useQuery({
    queryKey: ['disc-funnel', 30],
    queryFn: () => youtubeAPI.getDiscoveryFunnel(30),
    staleTime: 60000,
  });
  const { data: traffic } = useQuery({
    queryKey: ['disc-traffic', 30],
    queryFn: () => youtubeAPI.getTrafficSources(30),
    staleTime: 60000,
  });
  const { data: returning } = useQuery({
    queryKey: ['returning-viewers', 90],
    queryFn: () => youtubeAPI.getReturningViewers(90),
    staleTime: 120000,
  });

  const trafficChart = (traffic?.sources || [])
    .slice(0, 10)
    .map((s: any) => ({
      name: s.source || s.traffic_source,
      views: Number(s.views) || 0,
    }));

  const subBreakdown = (returning?.breakdown || []).map((r: any, i: number) => ({
    name: r.status,
    value: Number(r.views) || 0,
    color: COLORS[i % COLORS.length],
  }));

  return (
    <Box>
      <SectionTitle>DISCOVERY &amp; CTR FUNNEL</SectionTitle>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Panel label="ENGAGEMENT FUNNEL (30D)">
            {funnel?.funnel ? (
              <Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Typography variant="caption" color="textSecondary">VIEWS</Typography>
                    <Typography variant="h4" fontWeight={700}>{fmt(funnel.funnel.views)}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Typography variant="caption" color="textSecondary">ENGAGED VIEWS</Typography>
                    <Typography variant="h4" fontWeight={700}>{fmt(funnel.funnel.engaged_views)}</Typography>
                    <Typography variant="caption" color="primary">{funnel.funnel.engagement_rate_pct}% engagement rate</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Typography variant="caption" color="textSecondary">PREMIUM VIEWS</Typography>
                    <Typography variant="h4" fontWeight={700}>{fmt(funnel.funnel.red_views)}</Typography>
                    <Typography variant="caption" color="primary">{funnel.funnel.premium_rate_pct}% premium rate</Typography>
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <Typography variant="caption" color="textSecondary">AVG VIEW DURATION</Typography>
                    <Typography variant="h4" fontWeight={700}>{Math.round(funnel.funnel.avg_view_duration_seconds)}s</Typography>
                    <Typography variant="caption" color="textSecondary">{fmt(funnel.funnel.watch_minutes)} watch min</Typography>
                  </Grid>
                </Grid>
                {(funnel.daily || []).length > 0 && (
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={funnel.daily} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={(d) => d?.substring(5)} />
                      <YAxis tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : String(v)} />
                      <Tooltip formatter={(v: number) => fmt(v)} />
                      <Legend />
                      <Area type="monotone" dataKey="views" stackId="1" stroke="#5aaa5a" fill="#5aaa5a" fillOpacity={0.3} name="Views" />
                      <Area type="monotone" dataKey="engaged_views" stackId="2" stroke="#00ff41" fill="#00ff41" fillOpacity={0.4} name="Engaged" />
                      <Area type="monotone" dataKey="red_views" stackId="3" stroke="#ff6600" fill="#ff6600" fillOpacity={0.4} name="Premium" />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                  Note: Thumbnail impressions aren't exposed by YouTube's public APIs — this is the engagement funnel using real engaged_views data.
                </Typography>
              </Box>
            ) : funnel?.available === false ? (
              <Typography color="textSecondary">{funnel?.note || 'Not available'}</Typography>
            ) : (
              <Typography color="textSecondary">Loading...</Typography>
            )}
          </Panel>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="TRAFFIC SOURCES (30D)">
            {trafficChart.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={trafficChart} layout="vertical" margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : String(v)} />
                  <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v: number) => v.toLocaleString()} />
                  <Bar dataKey="views" fill="#00ff41" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="textSecondary">No traffic data in window.</Typography>
            )}
          </Panel>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Panel label="SUBSCRIBED VS UNSUBSCRIBED (90D)">
            {subBreakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={subBreakdown} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${(value ?? 0).toLocaleString()}`}>
                    {subBreakdown.map((entry: any, index: number) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => v.toLocaleString()} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="textSecondary">No data</Typography>
            )}
          </Panel>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DiscoveryInsights;
